import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    linkedSignal,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { s25Resource } from "../../../util/s25.resource";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { RenderComponentDirective } from "../../../standalone/render.component.directive";
import { Layouts } from "./se.layouts";
import { SeLayoutsService } from "./se.layouts.service";
import { S25Object } from "../../../util/core/s25.object";
import { S25Number } from "../../../util/core/s25.number";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { SeLayoutsStore } from "./se.layouts.store";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { LoginStore } from "../../../stores/login.store";
import { SeSecurityStore } from "../se.security.store";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import {
    ContactRoleOption,
    mapOrganizationRoleItemsToColumns,
} from "../securityConfig/se.security.baseline.config.store";
import { RoleRightsConfigService } from "../securityConfig/role.rights.config.service";
import { OrgRoleRights } from "../securityConfig/org.role.rights";
import { RoleRights } from "../securityConfig/role.rights";
import { AriaLive } from "../../../services/aria.live.service";
import { TelemetryService } from "../../../services/telemetry.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { NgTemplateOutlet } from "@angular/common";
import { TypedTemplateContextDirective } from "../../../standalone/typed.template.directive";

const PreviewHiddenReasons = {
    roleVisibility: { tag: "Role visibility", description: "hidden by role visibility" },
    permission: { tag: "Permission", description: "requires permission" },
} as const;
type PreviewHiddenReason = keyof typeof PreviewHiddenReasons;
type GhostTemplateContext = { name: string; reason: PreviewHiddenReason };

let previewSelectCount = 0;

@Component({
    standalone: true,
    selector: "s25-ng-se-layout",
    template: `
        <ng-template #ghostBlock [templateContext]="ghostContext" let-name="name" let-reason="reason">
            <div class="ghost-block">
                <s25-ng-icon [type]="'eyeOff'" [ariaLabel]="false" />
                <span class="visually-hidden"
                    >Hidden in preview: {{ name }}, {{ hiddenReasons[reason].description }}</span
                >
                <span class="ghost-name" aria-hidden="true">{{ name }}</span>
                <span class="ghost-tag" aria-hidden="true">{{ hiddenReasons[reason].tag }}</span>
            </div>
        </ng-template>
        @if (isLoading()) {
            <s25-ng-loading-inline-static />
        } @else if (!layout()) {
            Something went wrong
        } @else {
            @if (showPreviewToolbar()) {
                <div class="preview-toolbar" [class.preview-toolbar--active]="!!previewRole()">
                    <label class="preview-label" [attr.for]="previewSelectId">View as</label>
                    <select
                        #previewSelect
                        class="cn-form__control preview-select"
                        [id]="previewSelectId"
                        [disabled]="!previewRightsReady()"
                        (change)="onPreviewRoleChange(previewSelect.value)"
                    >
                        <option value="" [selected]="!previewRole()">All content</option>
                        @for (role of previewRoles(); track role.id) {
                            <option [value]="role.id" [selected]="role.id === previewRole()?.id">
                                {{ role.name }}
                            </option>
                        }
                    </select>
                    @if (previewRole(); as role) {
                        <span class="preview-status">Previewing as {{ role.name }}</span>
                        <s25-ng-button [type]="'outline'" [buttonText]="'Show all content'" (clicked)="exitPreview()" />
                    }
                    @if (previewRightsFailed()) {
                        <span class="preview-error">Couldn't load role permissions for preview</span>
                    }
                </div>
            }
            <div
                class="sections"
                [class.sections--preview]="!!previewRole()"
                [attr.role]="previewRole() ? 'region' : null"
                [attr.aria-label]="previewRole() ? 'Preview as ' + previewRole()!.name : null"
            >
                @for (section of sections(); track section.id) {
                    <div class="section" [class.section--hidden]="!!section.hiddenReason">
                        @if (section.hiddenReason; as reason) {
                            @if (!!section.heading) {
                                <h2 class="section-heading section-heading--hidden">
                                    <s25-ng-icon [type]="'eyeOff'" [ariaLabel]="false" />
                                    {{ section.heading }}
                                    <span class="visually-hidden"
                                        >, hidden in preview: {{ hiddenReasons[reason].description }}</span
                                    >
                                    <span class="ghost-tag" aria-hidden="true">{{ hiddenReasons[reason].tag }}</span>
                                </h2>
                            } @else {
                                <ng-container
                                    *ngTemplateOutlet="ghostBlock; context: { name: 'Untitled section', reason }"
                                />
                            }
                        } @else {
                            @if (!!section.heading) {
                                <h2 class="section-heading" [style]="section.headingCss">{{ section.heading }}</h2>
                            }
                            <div class="widgets" [style.--section-columns]="section.effectiveColumns">
                                @for (widget of section.widgets; track widget.id) {
                                    @if (widget.hiddenReason; as reason) {
                                        <ng-container
                                            *ngTemplateOutlet="ghostBlock; context: { name: widget.name, reason }"
                                        />
                                    } @else if (widget.flsAllowed) {
                                        <ng-container
                                            [render-component]="widget.meta.component"
                                            [inputs]="widget.meta.inputs"
                                        />
                                    } @else {
                                        <div
                                            class="locked-block"
                                            role="region"
                                            [attr.aria-label]="'Restricted content'"
                                        >
                                            <s25-ng-icon [type]="'lock'" [ariaLabel]="false" />
                                            <span>
                                                @if (loginStore.loggedIn()) {
                                                    You don't have access to this content
                                                } @else {
                                                    Sign in to view this content
                                                }
                                            </span>
                                        </div>
                                    }
                                }
                            </div>
                        }
                    </div>
                }
            </div>
        }
    `,
    styles: `
        :host {
            display: block;
            max-width: 1200px;
        }

        .section-heading {
            padding-bottom: 0.5rem;
            border-bottom: 2px solid var(--color--border);
        }

        .sections {
            display: grid;
            gap: 3rem;
        }

        .sections--preview {
            outline: 2px solid var(--color--border-bold);
            outline-offset: 0.75rem;
            border-radius: 2px;
        }

        .section {
            container-type: inline-size;
        }

        .widgets {
            display: grid;
            gap: 1rem;
            align-items: start;
            grid-template-columns: repeat(var(--section-columns, 1), minmax(0, 1fr));
        }

        @container (max-width: 655px) {
            .widgets {
                grid-template-columns: minmax(0, 1fr);
            }
        }

        :host
            ::ng-deep
            .section:not(:has(.widgets > * > *)):not(:has(.locked-block)):not(:has(.ghost-block)):not(
                .section--hidden
            ) {
            display: none;
        }

        .locked-block {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 1rem;
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 6px;
            color: var(--color--text-muted);
            font-size: 0.9em;
        }

        .preview-toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem 1rem;
            margin-bottom: 1.5rem;
            padding: 0.5rem 0.75rem;
            border: 1px solid var(--color--border);
            border-radius: 6px;
        }

        .preview-toolbar--active {
            background: var(--color--primary-flat-bg);
        }

        .preview-label {
            margin: 0;
            font-weight: 600;
        }

        .preview-select {
            width: auto;
            max-width: 300px;
        }

        .preview-error {
            color: var(--color--text-muted);
        }

        .ghost-block {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 1rem;
            border: 1px dashed var(--color--border);
            border-radius: 6px;
            color: var(--color--text-muted);
            font-size: 0.9em;
        }

        .section-heading--hidden {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
            color: var(--color--text-muted);
            --icon-size: 1em;
        }

        .ghost-tag {
            padding: 0.1rem 0.5rem;
            border: 1px solid var(--color--border);
            border-radius: 999px;
            font-size: 0.8rem;
            font-weight: normal;
        }

        @media (max-width: 768px) {
            .sections {
                gap: 2rem;
            }

            .preview-select {
                max-width: 100%;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25LoadingInlineStaticComponent,
        RenderComponentDirective,
        S25IconComponent,
        S25ButtonComponent,
        NgTemplateOutlet,
        TypedTemplateContextDirective,
    ],
})
export class SeLayoutComponent {
    // Provide either a page in an SeOrgSecurityStore context, or a layout id
    readonly page = input<Layouts.Page | undefined>(undefined);
    readonly layoutIdInput = input<number | undefined>(undefined, { alias: "layoutId" });

    protected readonly Layouts = Layouts;
    protected readonly hiddenReasons = PreviewHiddenReasons;
    protected readonly ghostContext: GhostTemplateContext;
    protected readonly previewSelectId = `se-layout-preview-role-${++previewSelectCount}`;
    readonly service = inject(SeLayoutsService);
    public store = inject(SeLayoutsStore);
    readonly orgSecurity = inject(SeOrgSecurityStore, { optional: true });
    protected readonly loginStore = inject(LoginStore);
    private readonly securityStore = inject(SeSecurityStore);
    private readonly previewSelect = viewChild<ElementRef<HTMLSelectElement>>("previewSelect");

    readonly orgId = computed(() => this.orgSecurity?.orgId());
    readonly layoutId = computed(() => {
        const layoutId = this.layoutIdInput();
        if (!!layoutId) return layoutId;
        const orgId = this.orgId();
        const page = this.page();
        if (!orgId || !page) return;
        return this.store.getOrgLayoutForPage(orgId, page)?.id;
    });
    readonly layoutResource = s25Resource({
        params: () => this.layoutId(),
        loader: async ({ params: layoutId, abortSignal }) => {
            return this.service.getLayout(layoutId, { abortSignal });
        },
    });
    readonly layout = computed(() => {
        const layoutId = this.layoutId();
        if (!!layoutId) {
            const error = this.layoutResource.error();
            const loaded = this.layoutResource.value();
            // Fallback to the default layout on load failure
            if (error || (!loaded && !this.layoutResource.isLoading())) {
                const page = this.page();
                return page ? Layouts.defaultLayouts[page] : undefined;
            }
            return loaded;
        }
        const page = this.page();
        if (!page) return;
        return Layouts.defaultLayouts[page];
    });

    readonly isLoading = computed(() => {
        if (this.layoutResource.resource.isLoading()) return true;
        return !!this.orgSecurity && (!this.orgId() || this.orgSecurity.orgRolesLoading());
    });

    /** Connect25 admins see every section and widget regardless of role visibility. */
    readonly isAdmin = computed(() => this.securityStore.hasFLSOverride());
    private readonly previewOrgId = computed(() => (this.isAdmin() ? this.orgId() : undefined));

    private readonly previewRolesResource = s25Resource({
        params: () => this.previewOrgId(),
        loader: async (): Promise<ContactRoleOption[]> =>
            mapOrganizationRoleItemsToColumns(await SearchCriteriaService.getOrganizationRoles(false, true)),
    });
    private readonly baselineRightsResource = s25Resource({
        params: () => this.previewOrgId(),
        loader: ({ params: orgId }) => RoleRightsConfigService.getRightsConfig(orgId),
    });
    private readonly roleOverridesResource = s25Resource({
        params: () => this.previewOrgId(),
        loader: ({ params: orgId }) => RoleRightsConfigService.getOrgRoleRightOverrides(orgId),
    });

    readonly previewRoles = computed(() => this.previewRolesResource.value() ?? []);
    readonly showPreviewToolbar = computed(() => !!this.previewOrgId() && !!this.previewRoles().length);
    readonly previewRightsReady = computed(
        () => !!this.baselineRightsResource.value() && !!this.roleOverridesResource.value(),
    );
    readonly previewRightsFailed = computed(
        () => !!this.baselineRightsResource.error() || !!this.roleOverridesResource.error(),
    );

    private readonly selectedPreviewRoleId = linkedSignal<number | undefined, number | undefined>({
        source: this.orgId,
        computation: () => undefined,
    });
    readonly previewRole = computed(() => {
        if (!this.previewRightsReady()) return undefined;
        const roleId = this.selectedPreviewRoleId();
        return this.previewRoles().find((role) => role.id === roleId);
    });

    readonly sections = computed(() => {
        const layout = this.layout();
        if (!layout) return [];
        const orgRoles = this.orgSecurity?.orgRoles();
        if (!!this.orgId() && !orgRoles) return []; // Wait for orgRoles to load
        const previewRole = this.previewRole();
        const seesEverything = this.isAdmin() && !previewRole;
        const viewerRoles = new Set(previewRole ? [previewRole.id] : orgRoles);
        const isVisible = (visibleToRoles?: number[]) =>
            seesEverything || !visibleToRoles?.length || visibleToRoles.some((role) => viewerRoles.has(role));
        // Outside preview, hidden content is dropped; in preview it stays so it can render as a placeholder
        const isRendered = (visibleToRoles?: number[]) => !!previewRole || isVisible(visibleToRoles);
        const widgetComponents = Layouts.pages[layout.page].widgets;
        return layout.sections
            .filter((section) => !!section.widgets.length && isRendered(section.visibleToRoles))
            .map((section) => {
                const sectionHiddenReason: PreviewHiddenReason | undefined = isVisible(section.visibleToRoles)
                    ? undefined
                    : "roleVisibility";
                const widgets = section.widgets
                    .filter((widget) => isRendered(widget.visibleToRoles))
                    .map((widget) => {
                        const component = widgetComponents[widget.type];
                        const defaultSettings = Layouts.getDefaultSettingsForWidget(component.META);
                        const settings = { ...defaultSettings, ...widget.settings };
                        const flsAllowed = this.allowsFlsFloor(component.META.flsFloor, previewRole);
                        let hiddenReason: PreviewHiddenReason | undefined;
                        if (!isVisible(widget.visibleToRoles)) hiddenReason = "roleVisibility";
                        else if (previewRole && !flsAllowed) hiddenReason = "permission";

                        return {
                            ...widget,
                            name: component.META.name,
                            settings,
                            flsAllowed,
                            hiddenReason,
                            meta: {
                                component,
                                inputs: {
                                    settingValues: S25Object.fromEntries(
                                        S25Object.entries(settings).map(([key, value]) => [key, value.value]),
                                    ),
                                    settings: component.META.settings.map((setting: Layouts.WidgetStaticSetting) =>
                                        Layouts.mapSettings(setting, settings),
                                    ),
                                },
                            },
                        };
                    });

                return {
                    ...section,
                    hiddenReason: sectionHiddenReason,
                    widgets,
                    // Saved layouts may omit columns or hold an unsupported count, so fall back into the supported range
                    effectiveColumns: S25Number.clamp(
                        section.columns ?? Layouts.MIN_SECTION_COLUMNS,
                        Layouts.MIN_SECTION_COLUMNS,
                        Layouts.MAX_SECTION_COLUMNS,
                    ),
                };
            })
            .filter((section) => !!section.hiddenReason || !!section.widgets.length);
    });

    onPreviewRoleChange(value: string) {
        const roleId = S25Number.parseInt(value);
        const role = this.previewRoles().find((option) => option.id === roleId);
        if (!role) {
            this.showAllContent();
            return;
        }
        this.selectedPreviewRoleId.set(role.id);
        void AriaLive.announce(`Previewing as ${role.name}`);
        TelemetryService.sendWithSub("StudentEngagement", "Layouts", "PreviewAsRole");
    }

    exitPreview() {
        // The exit button unmounts with the preview, so focus has to land somewhere stable first
        this.previewSelect()?.nativeElement.focus();
        this.showAllContent();
    }

    private showAllContent() {
        this.selectedPreviewRoleId.set(undefined);
        void AriaLive.announce("Showing all content");
        TelemetryService.sendWithSub("StudentEngagement", "Layouts", "PreviewExit");
    }

    private allowsFlsFloor(flsFloor: Layouts.OrgSecurityFlag | undefined, previewRole?: ContactRoleOption): boolean {
        if (!flsFloor || !this.orgSecurity) return true;
        if (!previewRole) return !!this.orgSecurity[flsFloor]?.();
        return this.orgSecurity.allowsForRole(flsFloor, (right) => this.previewLevelForRight(right, previewRole.id));
    }

    private previewLevelForRight(right: RoleRights.RoleRightsList, roleId: number) {
        return OrgRoleRights.effectiveLevel(
            this.baselineRightsResource.value() ?? [],
            this.roleOverridesResource.value() ?? [],
            this.orgId(),
            right,
            roleId,
        );
    }
}
